import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/pages/LandingPage";
import Seo from "@/components/Seo";
import WhatsAppButton from "@/components/WhatsAppButton";
import { terms } from "@/data/terms";
import { refund, privacy } from "@/data/refundPrivacy";

const POLICY_LINKS = [
  ["/terms", "Terms & Conditions"],
  ["/refund-policy", "Payment & Refund Policy"],
  ["/privacy-policy", "Privacy Policy"],
];

function PolicyBody({ policy }) {
  return (
    <article className="space-y-10" data-testid="policy-body">
      {policy.sections.map((sec, i) => (
        <section key={i}>
          {sec.h && <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mb-4">{sec.h}</h2>}
          <div className="space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
            {sec.paras?.map((p, j) => <p key={j}>{p}</p>)}
            {sec.items && (
              <ul className="list-disc pl-6 space-y-2">
                {sec.items.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            )}
          </div>
          {sec.table && (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    {sec.table.head.map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {sec.table.rows.map((row, j) => (
                    <tr key={j} className={j % 2 ? "bg-slate-50" : "bg-white"}>
                      {row.map((cell, k) => <td key={k} className="px-4 py-3 text-slate-600 align-top">{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed mt-3">
            {sec.parasAfter?.map((p, j) => <p key={j}>{p}</p>)}
          </div>
        </section>
      ))}
    </article>
  );
}

export function PolicyPage({ policy }) {
  return (
    <div className="min-h-screen bg-white" data-testid={`policy-page-${policy.slug}`}>
      <Seo title={`${policy.title} — SolveDesk`} description={`SolveDesk ${policy.title} for virtual office services across India.`} path={`/${policy.slug}`} />
      <WhatsAppButton />
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="policy-logo-link"><Logo /></Link>
          <Link to="/" data-testid="policy-back-link"
            className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="policy-title">{policy.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: {policy.updated}</p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="policy-nav">
            {POLICY_LINKS.map(([href, label]) => (
              <Link key={href} to={href} data-testid={`policy-nav-${href.slice(1)}`}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  href === `/${policy.slug}` ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {label}
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <PolicyBody policy={policy} />
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} solvedesk.space</p>
        </div>
      </footer>
    </div>
  );
}

export const TermsPage = () => <PolicyPage policy={terms} />;
export const RefundPage = () => <PolicyPage policy={refund} />;
export const PrivacyPage = () => <PolicyPage policy={privacy} />;
